/* @flow */

import classNames from 'classnames';
import { getContrast } from 'polished';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';

import styles from './BrandColorSection.style';

import { bgColor } from 'plugin/light/style/color';
import {
  getAACompliantColor,
  getAALargeCompliantColor,
  getColorLightTint,
  getReadableColor,
  getAlternateShade,
  getAlternateReadbleColor,
} from 'plugin/light/util/lightUtils';

type Props = {
  color: string,
};

const useStyles = createUseStyles(styles);

const BrandColorSection = ({ color }: Props): React.Node => {
  const aaColor = getAACompliantColor(color, bgColor);
  const aaLargeColor = getAALargeCompliantColor(color, bgColor);
  const lightColor = getColorLightTint(color);
  const aaLightColor = getAACompliantColor(color, lightColor);
  const readableColor = getReadableColor(color);
  const altColor = getAlternateShade(color);
  const altReadableColor = getAlternateReadbleColor(color);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({
    originalColor: color,
    aaColor,
    aaLargeColor,
    lightColor,
    aaLightColor,
    readableColor,
    altColor,
    altReadableColor,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <div className={classes.root}>
      <ul className={classNames(classes.list, classes.teal)}>
        <li className={classNames(classes.item, classes.original)}>
          <p className={classes.label}>Original</p>
          <div className={classes.swatch} />
          <dl className={classes.info}>
            <dd>Color</dd>
            <dt>
              {color} <br />
            </dt>
            <dd>Contrast</dd>
            <dt>{getContrast(color, bgColor)}</dt>
          </dl>
        </li>
        <li className={classNames(classes.item, classes.title)}>
          <p className={classes.label}>Title</p>
          <div className={classes.swatch}>Brand color</div>
          <dl className={classes.info}>
            <dd>Color</dd>
            <dt>
              {aaLargeColor} <br />
            </dt>
            <dd>Contrast</dd>
            <dt>{getContrast(aaLargeColor, bgColor)}</dt>
          </dl>
        </li>
        <li className={classNames(classes.item, classes.text)}>
          <p className={classes.label}>Text</p>
          <div className={classes.swatch}>Brand color</div>
          <dl className={classes.info}>
            <dd>Color</dd>
            <dt>
              {aaColor} <br />
            </dt>
            <dd>Contrast</dd>
            <dt>{getContrast(aaColor, bgColor)}</dt>
          </dl>
        </li>
        <li className={classNames(classes.item, classes.text2)}>
          <p className={classes.label}>Text on colored background</p>
          <div className={classes.swatch}>Brand color</div>
          <dl className={classes.info}>
            <dd>Color</dd>
            <dt>
              {aaLightColor} <br />
            </dt>
            <dd>Bg color</dd>
            <dt>
              {lightColor} <br />
            </dt>
            <dd>Contrast</dd>
            <dt>{getContrast(aaLightColor, lightColor)}</dt>
          </dl>
        </li>
        <li className={classNames(classes.item, classes.bg)}>
          <p className={classes.label}>Background</p>
          <div className={classes.swatch}>Brand color</div>
          <dl className={classes.info}>
            <dd>Color</dd>
            <dt>
              {color} <br />
            </dt>
            <dd>Contrast</dd>
            <dt>{getContrast(readableColor, color)}</dt>
          </dl>
        </li>
        <li className={classNames(classes.item, classes.bg2)}>
          <p className={classes.label}>Alternate background</p>
          <div className={classes.swatch}>Brand color</div>
          <dl className={classes.info}>
            <dd>Color</dd>
            <dt>
              {altColor} <br />
            </dt>
            <dd>Contrast</dd>
            <dt>{getContrast(altReadableColor, altColor)}</dt>
          </dl>
        </li>
      </ul>
    </div>
  );
};

export default compose(React.memo)(BrandColorSection);
